import { BaseError } from 'make-error';
import { z, ZodType, ZodTypeDef } from 'zod';
import { Log } from '../../AuxillaryService/Log';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyZodType = ZodType<any, ZodTypeDef, any>;

function pseudoValidate<T extends AnyZodType>(zodDef: T, value: unknown): z.infer<T> {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return zodDef.parse(value);
    } catch (error) {
        //Invalid!  Currently, we have no course correction, but at least we can be warned
        Log.Debug(`Pseudo Validate Warning: ${String(error)}`, null, __fileline);
    }

    return value;
}

export class ReachApiRetrievalError extends BaseError {
    data: {
        API: {
            jqXhr: JQueryXHR;
            textStatus: string;
            errorThrown: string;
        };
        CACHE: {
            message: string | undefined;
            event: Event | undefined;
        };
    };
    constructor(
        superMessage: string,
        jqXhr: JQueryXHR,
        textStatus: string,
        errorThrown: string,
        msg?: string,
        event?: Event
    ) {
        super(superMessage);
        this.data = {
            API: {
                jqXhr: jqXhr,
                textStatus: textStatus,
                errorThrown: errorThrown,
            },
            CACHE: {
                message: msg,
                event: event,
            },
        };
    }
}

/**
 * Convert `ReachAPI` `jQuery.Promise` results to Promises/A+ compatible
 * promises
 * @throws {@link ReachApiRetrievalError}
 */
function deferredToPromiseV2(deferred: JQueryDeferred<unknown>): Promise<unknown> {
    return new Promise(function (resolve, reject) {
        void deferred
            .done(function (data) {
                resolve(data);
            })
            .fail(function (
                jqXhr: JQueryXHR,
                textStatus: string,
                errorThrown: string,
                msg?: string,
                event?: Event
            ) {
                reject(
                    new ReachApiRetrievalError(
                        'ReachAPI Call Failed',
                        jqXhr,
                        textStatus,
                        errorThrown,
                        msg,
                        event
                    )
                );
            });
    });
}

/**
 * Convert {@link ReachAPI} functions to native promises and force the typing of the response.
 * @throws {@link ReachApiRetrievalError}
 */
export async function forceTypingDeferred<T extends AnyZodType>(
    zodDef: T,
    deferred: JQueryDeferred<unknown>
): Promise<z.infer<T>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return pseudoValidate<T>(zodDef, await deferredToPromiseV2(deferred));
}

/**
 * Force the typing of the response of {@link ReachAPI} functions that use
 * {@link ReachAPI.deferredToPromise}.
 * @throws {@link ReachApiRetrievalError}
 */
export async function forceTypingPromise<T extends AnyZodType>(
    zodDef: T,
    promise: Promise<{ data: unknown }>
): Promise<z.infer<T>> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return pseudoValidate<T>(zodDef, await promise);
}
